import React, { memo, useEffect, useState, useContext, useCallback } from 'react'
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import publicApi from '../services/public.api';
import LocationContext from '../context/LocationContext';

const SampleNextArrow = memo(function SampleNextArrow(props) {
    const { className, onClick } = props;
    return (
        <button
            type="button"
            onClick={onClick}
            className={` ${className} slick-next slick-arrow flex-center rounded-circle border border-gray-100 hover-border-main-600 text-xl hover-bg-main-600 hover-text-white transition-1`}
        >
            <i className="ph ph-caret-right" />
        </button>
    );
});

const SamplePrevArrow = memo(function SamplePrevArrow(props) {
    const { className, onClick } = props;
    return (
        <button
            type="button"
            onClick={onClick}
            className={`${className} slick-prev slick-arrow flex-center rounded-circle border border-gray-100 hover-border-main-600 text-xl hover-bg-main-600 hover-text-white transition-1`}
        >
            <i className="ph ph-caret-left" />
        </button>
    );
});

const FlashSalesOne = () => {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [countdowns, setCountdowns] = useState({});

    const context = useContext(LocationContext);
    const isCasaRegion = context?.isCasaRegion ?? false;
    const priceRegion = context?.priceRegion ?? 'fes';
    const [sliderKey, setSliderKey] = useState(priceRegion);

    useEffect(() => {
        setSliderKey(`slider-${priceRegion}-${Date.now()}`);
    }, [priceRegion]);

    const computePrice = (product) => {
        if (!product) return { basePrice: 0, priceCasa: 0, regularPrice: 0 };

        const priceCasa = parseFloat(product.price_casa || 0);
        const regularPrice = parseFloat(product.price || 0);
        const basePrice = (isCasaRegion && priceCasa > 0) ? priceCasa : regularPrice;

        return { basePrice, priceCasa, regularPrice };
    };

    const settings = {
        dots: false,
        arrows: true,
        infinite: true,
        speed: 1000,
        slidesToShow: 2,
        slidesToScroll: 1,
        initialSlide: 0,
        autoplay: true,
        nextArrow: <SampleNextArrow />,
        prevArrow: <SamplePrevArrow />,
        responsive: [
            {
                breakpoint: 991,
                settings: {
                    slidesToShow: 1,
                },
            },
        ],
    };

    useEffect(() => {
        const fetchOffers = async () => {
            try {
                const response = await publicApi.getOffers();
                if (response.success && Array.isArray(response.data)) {
                    setOffers(response.data);
                } else {
                    setOffers([]);
                }
            } catch (error) {
                console.error('Error fetching offers:', error);
                setOffers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchOffers();
    }, []);

    const calculateCountdown = useCallback((endDate) => {
        const end = new Date(endDate);
        const now = new Date();
        const diff = end - now;

        if (diff <= 0) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        return { days, hours, minutes, seconds };
    }, []);

    useEffect(() => {
        if (offers.length === 0) return;

        const updateCountdowns = () => {
            const newCountdowns = {};
            offers.forEach((offer) => {
                newCountdowns[offer.id] = calculateCountdown(offer.end_offre);
            });
            setCountdowns(newCountdowns);
        };

        updateCountdowns();
        const interval = setInterval(updateCountdowns, 1000);

        return () => clearInterval(interval);
    }, [offers, calculateCountdown]);

    const getProductImageUrl = (product) => {
        if (!product || !product.image) {
            return 'assets/images/thumbs/flash-sale-img1.png';
        }
        return publicApi.getImageUrl(product.image);
    };

    const bgImages = [
        'assets/images/bg/flash-sale-bg1.png',
        'assets/images/bg/flash-sale-bg2.png',
    ];

    // Hide the whole section when there are no active offers
    if (loading || offers.length === 0) {
        return null;
    }

    return (
        <section className="flash-sales pt-80" key={`flash-section-${priceRegion}`}>
            <div className="container container-lg">
                <div className="section-heading">
                    <div className="flex-between flex-wrap gap-8">
                        <h5 className="mb-0">Les Promos du Jour</h5>
                        <div className="flex-align gap-16 mr-point">
                            <Link
                                to="/shop"
                                className="text-sm fw-medium text-gray-700 hover-text-main-600 hover-text-decoration-underline"
                            >
                                Voir toutes les offres
                            </Link>
                        </div>
                    </div>
                </div>
                <div className="flash-sales__slider arrow-style-two">
                    <Slider {...settings} key={sliderKey}>
                        {offers.map((offer, index) => {
                            const countdown = countdowns[offer.id] || { days: 0, hours: 0, minutes: 0, seconds: 0 };
                            const bgImage = offer.bgImage || bgImages[index % bgImages.length];
                            const productImage = offer.productImage || getProductImageUrl(offer.product);
                            const product = offer.product;
                            const { basePrice } = computePrice(product);
                            const discount = parseFloat(offer.discount_percentage || 0);
                            const discountedPrice = discount > 0 ? basePrice * (1 - discount / 100) : basePrice;

                            return (
                                <div key={`${offer.id}-${priceRegion}`}>
                                    <div
                                        className="flash-sales-item rounded-16 overflow-hidden z-1 position-relative flex-align flex-0 justify-content-between gap-8"
                                        style={{ minHeight: '320px' }}
                                    >
                                        <img
                                            src={bgImage}
                                            alt=""
                                            className="position-absolute inset-block-start-0 inset-inline-start-0 w-100 h-100 object-fit-cover z-n1 flash-sales-item__bg"
                                        />
                                        <div
                                            className="flash-sales-item__thumb d-sm-block d-none"
                                            style={{ minWidth: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <img
                                                src={productImage}
                                                alt=""
                                                className="flash-sales-thumb-img"
                                                style={{
                                                    width: '320px',
                                                    height: '320px',
                                                    objectFit: 'contain',
                                                    maxWidth: 'none',
                                                }}
                                                onError={(e) => {
                                                    e.target.src = 'assets/images/thumbs/flash-sale-img1.png';
                                                }}
                                            />
                                        </div>
                                        <div className="flash-sales-item__content ms-sm-auto">
                                            <h6 className="flash-sales-title text-32 mb-20">{offer.title}</h6>
                                            <div className="countdown">
                                                <ul className="countdown-list flex-align" style={{ flexWrap: 'nowrap', gap: '8px' }}>
                                                    <li className="countdown-list__item text-heading flex-align gap-4 text-sm fw-medium" style={{ whiteSpace: 'nowrap' }}>
                                                        <span className="days" />
                                                        {countdown.days}  Jours
                                                    </li>
                                                    <li className="countdown-list__item text-heading flex-align gap-4 text-sm fw-medium" style={{ whiteSpace: 'nowrap' }}>
                                                        <span className="hours" />
                                                        {countdown.hours}  Heures
                                                    </li>
                                                    <li className="countdown-list__item text-heading flex-align gap-4 text-sm fw-medium" style={{ whiteSpace: 'nowrap' }}>
                                                        <span className="minutes" />
                                                        {countdown.minutes}  Min
                                                    </li>
                                                    <li className="countdown-list__item text-heading flex-align gap-4 text-sm fw-medium" style={{ whiteSpace: 'nowrap' }}>
                                                        <span className="seconds" />
                                                        {countdown.seconds}  Sec
                                                    </li>
                                                </ul>
                                            </div>
                                            <Link
                                                to={offer.product ? `/product-details/${offer.product.id}` : '/shop'}
                                                className="btn flash-sales-btn d-inline-flex align-items-center rounded-pill gap-8 mt-24"
                                            >
                                                Achetez maintenant
                                                <span className="icon text-xl d-flex">
                                                    <i className="ph ph-arrow-right" />
                                                </span>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </Slider>
                </div>
            </div>
        </section>
    );
};

export default FlashSalesOne;
